/**
 * Coach Program Builder — create/edit a training program.
 */
import { useState, useEffect, useCallback } from 'react';
import {
  View, Text, StyleSheet, ScrollView, TouchableOpacity, TextInput,
  ActivityIndicator, Alert, Modal, FlatList,
} from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useLocalSearchParams, useRouter } from 'expo-router';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { TYPO } from '@/constants/typography';
import {
  loadPrograms,
  saveProgram,
  assignProgram,
  type Program,
  type ProgramDay,
  type ProgramExercise,
} from '@/lib/programBuilder';
import { supabase } from '@/lib/supabase';
import {
  ArrowLeft, Plus, Trash2, ChevronDown, ChevronRight, Users, Save,
} from 'lucide-react-native';

function generateId(): string {
  return Date.now().toString(36) + Math.random().toString(36).slice(2, 6);
}

function emptyExercise(): ProgramExercise {
  return {
    id: generateId(),
    exerciseName: '',
    targetSets: 3,
    targetReps: 10,
    targetWeight: undefined,
    rpe: undefined,
    notes: undefined,
  };
}

function emptyDay(dayNumber: number): ProgramDay {
  return {
    id: generateId(),
    name: `Day ${dayNumber}`,
    exercises: [],
  };
}

interface ClientLink {
  id: string;
  name: string;
  athleteId: string;
}

export default function CoachProgramBuilder() {
  const { theme } = useTheme();
  const c = theme.colors;
  const router = useRouter();
  const { profile } = useAuth();
  const { programId } = useLocalSearchParams<{ programId?: string }>();

  const [programName, setProgramName] = useState('');
  const [days, setDays] = useState<ProgramDay[]>([]);
  const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(!!programId);
  const [saving, setSaving] = useState(false);
  const [originalProgram, setOriginalProgram] = useState<Program | null>(null);

  // Assign modal
  const [showAssign, setShowAssign] = useState(false);
  const [clients, setClients] = useState<ClientLink[]>([]);
  const [loadingClients, setLoadingClients] = useState(false);
  const [assigning, setAssigning] = useState<string | null>(null);

  // Load existing program
  const loadProgram = useCallback(async () => {
    if (!programId || !profile?.id) return;
    try {
      const all = await loadPrograms(profile.id);
      const prog = all.find((p) => p.id === programId);
      if (prog) {
        setOriginalProgram(prog);
        setProgramName(prog.name);
        setDays(prog.days);
        // Expand first day by default
        if (prog.days.length > 0) {
          setExpandedDays(new Set([prog.days[0].id]));
        }
      }
    } catch (err) {
      console.warn('[ProgramBuilder] load error', err);
    } finally {
      setLoading(false);
    }
  }, [programId, profile?.id]);

  useEffect(() => {
    if (programId) {
      loadProgram();
    } else {
      // New program — start with one empty day
      const d = emptyDay(1);
      setDays([d]);
      setExpandedDays(new Set([d.id]));
    }
  }, [programId, loadProgram]);

  // Load clients for assign modal
  const loadClients = useCallback(async () => {
    if (!profile?.id) return;
    setLoadingClients(true);
    try {
      const { data } = await supabase
        .from('coach_athlete_links')
        .select('id, athlete_user_id, profiles:athlete_user_id(display_name, handle)')
        .eq('coach_user_id', profile.id)
        .eq('status', 'active');

      const list: ClientLink[] = (data ?? []).map((row: any) => ({
        id: row.id,
        athleteId: row.athlete_user_id,
        name:
          row.profiles?.display_name ||
          row.profiles?.handle ||
          row.athlete_user_id,
      }));
      setClients(list);
    } catch (err) {
      console.warn('[ProgramBuilder] loadClients error', err);
    } finally {
      setLoadingClients(false);
    }
  }, [profile?.id]);

  const handleSave = async () => {
    if (!profile?.id) return;
    if (!programName.trim()) {
      Alert.alert('Name required', 'Please enter a program name.');
      return;
    }
    setSaving(true);
    try {
      const prog: Program = {
        id: originalProgram?.id ?? generateId(),
        name: programName.trim(),
        days,
        createdAt: originalProgram?.createdAt ?? new Date().toISOString(),
        coachId: profile.id,
        assignedTo: originalProgram?.assignedTo,
      };
      await saveProgram(prog);
      setOriginalProgram(prog);
      Alert.alert('Saved', 'Program saved successfully.');
    } catch (err: any) {
      Alert.alert('Save failed', err.message || 'Could not save program.');
    } finally {
      setSaving(false);
    }
  };

  const handleAssign = async (athleteId: string) => {
    if (!originalProgram) {
      Alert.alert('Save first', 'Please save the program before assigning it.');
      return;
    }
    setAssigning(athleteId);
    try {
      await assignProgram(originalProgram.id, athleteId);
      Alert.alert('Assigned', 'Program assigned successfully.');
    } catch (err: any) {
      Alert.alert('Assign failed', err.message || 'Could not assign program.');
    } finally {
      setAssigning(null);
    }
  };

  /* ── Day helpers ── */
  const addDay = () => {
    const d = emptyDay(days.length + 1);
    setDays((prev) => [...prev, d]);
    setExpandedDays((prev) => new Set([...prev, d.id]));
  };

  const removeDay = (dayId: string) => {
    setDays((prev) => prev.filter((d) => d.id !== dayId));
    setExpandedDays((prev) => {
      const next = new Set(prev);
      next.delete(dayId);
      return next;
    });
  };

  const toggleDayExpand = (dayId: string) => {
    setExpandedDays((prev) => {
      const next = new Set(prev);
      if (next.has(dayId)) next.delete(dayId);
      else next.add(dayId);
      return next;
    });
  };

  const updateDayName = (dayId: string, name: string) => {
    setDays((prev) => prev.map((d) => (d.id === dayId ? { ...d, name } : d)));
  };

  /* ── Exercise helpers ── */
  const addExercise = (dayId: string) => {
    const ex = emptyExercise();
    setDays((prev) =>
      prev.map((d) =>
        d.id === dayId ? { ...d, exercises: [...d.exercises, ex] } : d,
      ),
    );
  };

  const removeExercise = (dayId: string, exId: string) => {
    setDays((prev) =>
      prev.map((d) =>
        d.id === dayId
          ? { ...d, exercises: d.exercises.filter((e) => e.id !== exId) }
          : d,
      ),
    );
  };

  const updateExercise = (dayId: string, exId: string, patch: Partial<ProgramExercise>) => {
    setDays((prev) =>
      prev.map((d) =>
        d.id === dayId
          ? { ...d, exercises: d.exercises.map((e) => (e.id === exId ? { ...e, ...patch } : e)) }
          : d,
      ),
    );
  };

  if (loading) {
    return (
      <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
        <ActivityIndicator color={c.accent} style={{ marginTop: 60 }} />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={[styles.safe, { backgroundColor: c.background }]}>
      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          onPress={() => router.back()}
          hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
          style={styles.backBtn}
        >
          <ArrowLeft size={22} color={c.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: c.text }]} numberOfLines={1}>
          {programId ? 'Edit Program' : 'New Program'}
        </Text>
        <View style={styles.headerActions}>
          {originalProgram && (
            <TouchableOpacity
              style={[styles.headerBtn, { backgroundColor: c.surface, borderColor: c.border }]}
              onPress={() => {
                loadClients();
                setShowAssign(true);
              }}
            >
              <Users size={15} color={c.accent} />
              <Text style={[styles.headerBtnText, { color: c.accent }]}>Assign</Text>
            </TouchableOpacity>
          )}
          <TouchableOpacity
            style={[styles.headerBtn, { backgroundColor: c.accent }]}
            onPress={handleSave}
            disabled={saving}
          >
            {saving ? (
              <ActivityIndicator color={c.accentForeground} size="small" />
            ) : (
              <>
                <Save size={15} color={c.accentForeground} />
                <Text style={[styles.headerBtnText, { color: c.accentForeground }]}>Save</Text>
              </>
            )}
          </TouchableOpacity>
        </View>
      </View>

      <ScrollView contentContainerStyle={styles.scroll} keyboardShouldPersistTaps="handled">
        {/* Program Name */}
        <TextInput
          style={[styles.nameInput, { backgroundColor: c.surface, borderColor: c.border, color: c.text }]}
          placeholder="Program name..."
          placeholderTextColor={c.textTertiary}
          value={programName}
          onChangeText={setProgramName}
          maxLength={80}
        />

        {/* Days */}
        {days.map((day) => {
          const expanded = expandedDays.has(day.id);
          return (
            <View key={day.id} style={[styles.dayCard, { backgroundColor: c.surface, borderColor: c.border }]}>
              {/* Day header */}
              <View style={styles.dayHeader}>
                <TouchableOpacity onPress={() => toggleDayExpand(day.id)} style={{ flex: 1, flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                  {expanded
                    ? <ChevronDown size={18} color={c.textSecondary} />
                    : <ChevronRight size={18} color={c.textSecondary} />}
                  <TextInput
                    style={[styles.dayNameInput, { color: c.text }]}
                    value={day.name}
                    onChangeText={(t) => updateDayName(day.id, t)}
                    placeholder="Day name"
                    placeholderTextColor={c.textTertiary}
                    maxLength={40}
                  />
                </TouchableOpacity>
                <Text style={[styles.dayExCount, { color: c.textTertiary }]}>
                  {day.exercises.length} ex.
                </Text>
                <TouchableOpacity
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  onPress={() => removeDay(day.id)}
                >
                  <Trash2 size={15} color={c.textTertiary} />
                </TouchableOpacity>
              </View>

              {/* Exercises */}
              {expanded && (
                <View style={styles.exercisesList}>
                  {day.exercises.map((ex, idx) => (
                    <ExerciseRow
                      key={ex.id}
                      exercise={ex}
                      index={idx}
                      colors={c}
                      onChange={(patch) => updateExercise(day.id, ex.id, patch)}
                      onRemove={() => removeExercise(day.id, ex.id)}
                    />
                  ))}
                  <TouchableOpacity
                    style={[styles.addExBtn, { borderColor: c.border }]}
                    onPress={() => addExercise(day.id)}
                  >
                    <Plus size={14} color={c.accent} />
                    <Text style={[styles.addExBtnText, { color: c.accent }]}>Add Exercise</Text>
                  </TouchableOpacity>
                </View>
              )}
            </View>
          );
        })}

        {/* Add Day */}
        <TouchableOpacity
          style={[styles.addDayBtn, { borderColor: c.border }]}
          onPress={addDay}
        >
          <Plus size={16} color={c.accent} />
          <Text style={[styles.addDayBtnText, { color: c.accent }]}>Add Day</Text>
        </TouchableOpacity>

        <View style={{ height: 60 }} />
      </ScrollView>

      {/* Assign Modal */}
      <Modal visible={showAssign} animationType="slide" transparent onRequestClose={() => setShowAssign(false)}>
        <View style={styles.modalBackdrop}>
          <View style={[styles.modalSheet, { backgroundColor: c.background }]}>
            <View style={styles.modalHeader}>
              <Text style={[styles.modalTitle, { color: c.text }]}>Assign to Client</Text>
              <TouchableOpacity onPress={() => setShowAssign(false)}>
                <Text style={{ color: c.textTertiary, fontSize: 15 }}>Close</Text>
              </TouchableOpacity>
            </View>
            {loadingClients ? (
              <ActivityIndicator color={c.accent} style={{ marginTop: 30 }} />
            ) : clients.length === 0 ? (
              <Text style={[styles.modalEmpty, { color: c.textSecondary }]}>No active clients found.</Text>
            ) : (
              <FlatList
                data={clients}
                keyExtractor={(item) => item.athleteId}
                contentContainerStyle={{ paddingHorizontal: 16, paddingBottom: 40 }}
                renderItem={({ item }) => (
                  <View style={[styles.clientRow, { borderColor: c.border }]}>
                    <Text style={[styles.clientName, { color: c.text }]}>{item.name}</Text>
                    <TouchableOpacity
                      style={[styles.assignBtn, { backgroundColor: c.accent, opacity: assigning === item.athleteId ? 0.6 : 1 }]}
                      onPress={() => handleAssign(item.athleteId)}
                      disabled={!!assigning}
                    >
                      {assigning === item.athleteId ? (
                        <ActivityIndicator color={c.accentForeground} size="small" />
                      ) : (
                        <Text style={[styles.assignBtnText, { color: c.accentForeground }]}>Assign</Text>
                      )}
                    </TouchableOpacity>
                  </View>
                )}
              />
            )}
          </View>
        </View>
      </Modal>
    </SafeAreaView>
  );
}

/* ── Exercise Row ── */
interface ExerciseRowProps {
  exercise: ProgramExercise;
  index: number;
  colors: any;
  onChange: (patch: Partial<ProgramExercise>) => void;
  onRemove: () => void;
}

function ExerciseRow({ exercise, index, colors: c, onChange, onRemove }: ExerciseRowProps) {
  return (
    <View style={[styles.exRow, { borderColor: c.border }]}>
      <View style={styles.exRowTop}>
        <Text style={[styles.exIndex, { color: c.textTertiary }]}>{index + 1}.</Text>
        <TextInput
          style={[styles.exNameInput, { color: c.text, borderColor: c.border }]}
          placeholder="Exercise name"
          placeholderTextColor={c.textTertiary}
          value={exercise.exerciseName}
          onChangeText={(t) => onChange({ exerciseName: t })}
        />
        <TouchableOpacity hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }} onPress={onRemove}>
          <Trash2 size={14} color={c.textTertiary} />
        </TouchableOpacity>
      </View>
      <View style={styles.exRowParams}>
        <View style={styles.paramCell}>
          <Text style={[styles.paramLabel, { color: c.textTertiary }]}>Sets</Text>
          <TextInput
            style={[styles.paramInput, { color: c.text, borderColor: c.border, backgroundColor: c.surfaceVariant }]}
            value={String(exercise.targetSets)}
            onChangeText={(t) => onChange({ targetSets: parseInt(t, 10) || 0 })}
            keyboardType="numeric"
            maxLength={3}
          />
        </View>
        <View style={styles.paramCell}>
          <Text style={[styles.paramLabel, { color: c.textTertiary }]}>Reps</Text>
          <TextInput
            style={[styles.paramInput, { color: c.text, borderColor: c.border, backgroundColor: c.surfaceVariant }]}
            value={String(exercise.targetReps)}
            onChangeText={(t) => onChange({ targetReps: parseInt(t, 10) || 0 })}
            keyboardType="numeric"
            maxLength={3}
          />
        </View>
        <View style={styles.paramCell}>
          <Text style={[styles.paramLabel, { color: c.textTertiary }]}>Weight</Text>
          <TextInput
            style={[styles.paramInput, { color: c.text, borderColor: c.border, backgroundColor: c.surfaceVariant }]}
            value={exercise.targetWeight != null ? String(exercise.targetWeight) : ''}
            placeholder="opt."
            placeholderTextColor={c.textTertiary}
            onChangeText={(t) => onChange({ targetWeight: t ? parseFloat(t) : undefined })}
            keyboardType="numeric"
            maxLength={6}
          />
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  safe: { flex: 1 },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    gap: 10,
  },
  backBtn: { padding: 4 },
  headerTitle: { flex: 1, fontSize: 18, fontFamily: TYPO.heading.bold },
  headerActions: { flexDirection: 'row', gap: 8 },
  headerBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 2,
    borderWidth: 1,
  },
  headerBtnText: { fontSize: 13, fontFamily: TYPO.body.bold },
  scroll: { padding: 16 },
  nameInput: {
    borderWidth: 1,
    borderRadius: 2,
    padding: 14,
    fontSize: 18,
    fontFamily: TYPO.heading.bold,
    marginBottom: 16,
  },
  dayCard: {
    borderWidth: 1,
    borderRadius: 2,
    marginBottom: 12,
    overflow: 'hidden',
  },
  dayHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 10,
    gap: 8,
  },
  dayNameInput: {
    flex: 1,
    fontSize: 15,
    fontFamily: TYPO.body.bold,
  },
  dayExCount: {
    fontSize: 12,
    fontFamily: TYPO.body.regular,
    minWidth: 30,
    textAlign: 'right',
  },
  exercisesList: {
    paddingHorizontal: 12,
    paddingBottom: 12,
    gap: 8,
  },
  exRow: {
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingTop: 10,
    gap: 8,
  },
  exRowTop: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
  },
  exIndex: {
    fontSize: 12,
    fontFamily: TYPO.body.semibold,
    minWidth: 18,
  },
  exNameInput: {
    flex: 1,
    fontSize: 14,
    fontFamily: TYPO.body.regular,
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingVertical: 4,
  },
  exRowParams: {
    flexDirection: 'row',
    gap: 10,
    paddingLeft: 24,
  },
  paramCell: {
    gap: 2,
  },
  paramLabel: {
    fontSize: 10,
    fontFamily: TYPO.body.semibold,
    letterSpacing: 0.5,
  },
  paramInput: {
    borderWidth: 1,
    borderRadius: 2,
    paddingHorizontal: 8,
    paddingVertical: 6,
    fontSize: 13,
    fontFamily: TYPO.body.regular,
    width: 60,
    textAlign: 'center',
  },
  addExBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderRadius: 2,
    paddingVertical: 8,
    paddingHorizontal: 12,
    marginTop: 6,
  },
  addExBtnText: {
    fontSize: 13,
    fontFamily: TYPO.body.semibold,
  },
  addDayBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    borderStyle: 'dashed',
    borderRadius: 2,
    paddingVertical: 14,
    marginTop: 4,
  },
  addDayBtnText: {
    fontSize: 15,
    fontFamily: TYPO.body.bold,
  },
  // Modal
  modalBackdrop: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'flex-end',
  },
  modalSheet: {
    borderTopLeftRadius: 12,
    borderTopRightRadius: 12,
    maxHeight: '70%',
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: '#333',
  },
  modalTitle: {
    fontSize: 16,
    fontFamily: TYPO.heading.bold,
  },
  modalEmpty: {
    textAlign: 'center',
    paddingVertical: 30,
    fontSize: 14,
  },
  clientRow: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingVertical: 12,
    borderBottomWidth: StyleSheet.hairlineWidth,
    gap: 12,
  },
  clientName: {
    flex: 1,
    fontSize: 14,
    fontFamily: TYPO.body.semibold,
  },
  assignBtn: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 2,
    minWidth: 70,
    alignItems: 'center',
  },
  assignBtnText: {
    fontSize: 13,
    fontFamily: TYPO.body.bold,
  },
});
